import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Checkbox from "../Form/Checkbox";
import PlatformLogo from "../Common/PlatformLogo";
import TableContainer from "../Tables/TableContainer";
import TableCell from "../../pages/BillingAndPayments/components/TableCell";
import { useTranslationApp } from "../../lib/i18next";
import {
  AGENCY_ACCOUNT_LINKING_COLUMN_DETAILS,
  AGENCY_ACCOUNT_LINKING_COLUMN_STATUS,
  AGENCY_ACCOUNT_LINKING_PLATFORM_GOOGLE,
  AGENCY_ACCOUNT_LINKING_PLATFORM_META,
  AGENCY_ACCOUNT_LINKING_PLATFORM_TIKTOK,
  AGENCY_ACCOUNT_LINKING_SHEET_TITLE,
  COLUMN_NAME_USER,
  MODAL_INTEGRATION_NOT_FOUND_COLUMN_AD_ACCOUNT,
  PLATFORM,
  SELECT_ALL,
} from "../../i18n/keysTranslations";
import {
  AGENCY_LINKING_PLATFORM,
  AGENCY_LINKING_STATUS,
  agencyAccountLinkDetailKey,
  agencyAccountLinkStatus,
  agencyAccountLinkStatusKey,
  isAgencyAccountSelectable,
} from "../../utils/agencyAccountLinking";
import { SX_CHECKBOX_FOCUS_VISIBLE } from "./agencyLinkingStyles";

const CHECKBOX_CELL_SX = {
  width: 52,
  minWidth: 52,
  maxWidth: 52,
  px: "4px !important",
  cursor: "default",
};

const PLATFORM_CELL_SX = {
  width: 124,
  minWidth: 124,
  maxWidth: 160,
  cursor: "default",
};

const TEXT_CELL_SX = {
  minWidth: 160,
  whiteSpace: "normal",
  wordBreak: "break-word",
  height: "auto",
  overflow: "visible",
  textOverflow: "clip",
};

const STATUS_CELL_SX = {
  width: 148,
  minWidth: 140,
  whiteSpace: "nowrap",
  cursor: "default",
};

const DETAILS_CELL_SX = {
  minWidth: 220,
  whiteSpace: "normal",
  wordBreak: "break-word",
  height: "auto",
  overflow: "visible",
  textOverflow: "clip",
};

const STATUS_CHIP_TONE = Object.freeze({
  [AGENCY_LINKING_STATUS.UNLINKED]: {
    bgcolor: "rgba(158, 158, 158, 0.1)",
    borderColor: "rgba(158, 158, 158, 0.3)",
  },
  [AGENCY_LINKING_STATUS.PENDING]: {
    bgcolor: "rgba(255, 193, 7, 0.1)",
    borderColor: "rgba(255, 193, 7, 0.3)",
  },
  [AGENCY_LINKING_STATUS.FAILED]: {
    bgcolor: "rgba(197, 34, 31, 0.1)",
    borderColor: "rgba(197, 34, 31, 0.3)",
  },
  [AGENCY_LINKING_STATUS.LINKED]: {
    bgcolor: "rgba(19, 115, 51, 0.1)",
    borderColor: "rgba(19, 115, 51, 0.3)",
  },
});

const statusChipTone = (status) =>
  STATUS_CHIP_TONE[status] || STATUS_CHIP_TONE[AGENCY_LINKING_STATUS.UNLINKED];

const agencyLinkingPlatformLabelKey = (platform) => {
  switch (platform) {
    case AGENCY_LINKING_PLATFORM.META:
      return AGENCY_ACCOUNT_LINKING_PLATFORM_META;
    case AGENCY_LINKING_PLATFORM.GOOGLE:
      return AGENCY_ACCOUNT_LINKING_PLATFORM_GOOGLE;
    case AGENCY_LINKING_PLATFORM.TIKTOK:
      return AGENCY_ACCOUNT_LINKING_PLATFORM_TIKTOK;
    default: {
      const _unexpected = platform;
      void _unexpected;
      return null;
    }
  }
};

const ClientAccountsTable = ({
  accounts = [],
  selectedIds,
  disabled = false,
  onToggleAccount = () => {},
  onToggleAll = () => {},
}) => {
  const { t } = useTranslationApp();
  const selectableAccounts = accounts.filter(isAgencyAccountSelectable);
  const selectedCount = selectableAccounts.filter((account) =>
    selectedIds.has(account.id)
  ).length;
  const headerChecked =
    selectableAccounts.length > 0 &&
    selectedCount === selectableAccounts.length;
  const headerIndeterminate =
    selectedCount > 0 && selectedCount < selectableAccounts.length;
  const platformLabel = (platform) => {
    const labelKey = agencyLinkingPlatformLabelKey(platform);
    return labelKey ? t(labelKey) : "";
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
      <TableContainer
        page={0}
        onPageChange={() => {}}
        rowsPerPage={Math.max(accounts.length, 1)}
        keys={accounts}
        hasPagination={false}
        ariaLabel={t(AGENCY_ACCOUNT_LINKING_SHEET_TITLE)}
        sx={{
          maxHeight: "min(560px, calc(100vh - 260px))",
          borderRadius: "0px !important",
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell isHeader resizable={false} sx={CHECKBOX_CELL_SX}>
              <Checkbox
                indeterminate={headerIndeterminate}
                checked={headerChecked}
                disabled={disabled || selectableAccounts.length === 0}
                onChange={(event) => onToggleAll(event.target.checked)}
                inputProps={{ "aria-label": t(SELECT_ALL) }}
                sx={SX_CHECKBOX_FOCUS_VISIBLE}
              />
            </TableCell>
            <TableCell isHeader sx={{ cursor: "default", minWidth: 180 }}>
              {t(COLUMN_NAME_USER)}
            </TableCell>
            <TableCell
              isHeader
              align="center"
              sx={{ ...PLATFORM_CELL_SX, cursor: "default" }}
            >
              {t(PLATFORM)}
            </TableCell>
            <TableCell isHeader sx={{ cursor: "default", minWidth: 160 }}>
              {t(MODAL_INTEGRATION_NOT_FOUND_COLUMN_AD_ACCOUNT)}
            </TableCell>
            <TableCell isHeader sx={STATUS_CELL_SX}>
              {t(AGENCY_ACCOUNT_LINKING_COLUMN_STATUS)}
            </TableCell>
            <TableCell isHeader sx={{ ...DETAILS_CELL_SX, cursor: "default" }}>
              {t(AGENCY_ACCOUNT_LINKING_COLUMN_DETAILS)}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {accounts.map((account, index) => {
            const selectable = isAgencyAccountSelectable(account);
            const checked = selectable && selectedIds.has(account.id);
            const isLastRow = index === accounts.length - 1;
            const label = platformLabel(account.platform);
            const rowDisabled = disabled || !selectable;
            const rowCursor = rowDisabled ? "default" : "pointer";
            const status = agencyAccountLinkStatus(account);
            const tone = statusChipTone(status);
            const detailKey = agencyAccountLinkDetailKey(account);
            return (
              <TableRow
                key={account.id}
                selected={checked}
                onClick={() => {
                  if (rowDisabled) return;
                  onToggleAccount(account.id, !checked);
                }}
                sx={{ cursor: rowCursor }}
              >
                <TableCell
                  indexRow={index}
                  isLastRow={isLastRow}
                  sx={CHECKBOX_CELL_SX}
                >
                  <Checkbox
                    checked={checked}
                    disabled={rowDisabled}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) => {
                      if (rowDisabled) return;
                      onToggleAccount(account.id, event.target.checked);
                    }}
                    inputProps={{
                      "aria-label": `${account.integrationName}, ${label}, ${account.name}`,
                    }}
                    sx={SX_CHECKBOX_FOCUS_VISIBLE}
                  />
                </TableCell>
                <TableCell
                  indexRow={index}
                  isLastRow={isLastRow}
                  sx={{ ...TEXT_CELL_SX, cursor: rowCursor }}
                >
                  {account.integrationName}
                </TableCell>
                <TableCell
                  indexRow={index}
                  isLastRow={isLastRow}
                  align="center"
                  sx={{ ...PLATFORM_CELL_SX, cursor: rowCursor }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PlatformLogo
                      platform={account.platform}
                      width={22}
                      height={22}
                      alt={label}
                    />
                  </Box>
                </TableCell>
                <TableCell
                  indexRow={index}
                  isLastRow={isLastRow}
                  sx={{ ...TEXT_CELL_SX, cursor: rowCursor }}
                >
                  {account.name}
                </TableCell>
                <TableCell
                  indexRow={index}
                  isLastRow={isLastRow}
                  sx={{ ...STATUS_CELL_SX, cursor: rowCursor }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      px: 1.25,
                      py: 0.25,
                      lineHeight: 1.2,
                      borderRadius: 1,
                      bgcolor: tone.bgcolor,
                      border: `1px solid ${tone.borderColor}`,
                      color: "#333333",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      fontSize: "0.7rem",
                      whiteSpace: "nowrap",
                      display: "inline-block",
                    }}
                  >
                    {t(agencyAccountLinkStatusKey(account))}
                  </Typography>
                </TableCell>
                <TableCell
                  indexRow={index}
                  isLastRow={isLastRow}
                  sx={{
                    ...DETAILS_CELL_SX,
                    cursor: rowCursor,
                    color: "text.secondary",
                  }}
                >
                  {detailKey ? t(detailKey) : "-"}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </TableContainer>
    </Box>
  );
};

export default ClientAccountsTable;
